import { seriesImageCount, seriesLoopInstanceNumber } from '@/lib/viewer/seriesShape';
/** Strip characters that are unsafe in file names across Windows/macOS/Linux. */
export function sanitizeFilename(raw, fallback = 'study') {
    const cleaned = (raw ?? '')
        // DICOM person names use `^` as a component separator.
        .replace(/\^/g, ' ')
        .replace(/[<>:"/\\|?*]/g, ' ')
        // Strip ASCII control characters (unsafe in file names).
        // eslint-disable-next-line no-control-regex -- intentional control-char strip
        .replace(/[\u0000-\u001f]/g, ' ')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^[._]+|[._]+$/g, '')
        .slice(0, 80);
    return cleaned || fallback;
}
/** Format a study date as YYYY-MM-DD for file names (falls back to empty). */
function studyDatePart(dateTime) {
    if (!dateTime)
        return '';
    const d = new Date(dateTime);
    if (Number.isNaN(d.getTime()))
        return '';
    return d.toISOString().slice(0, 10);
}
/**
 * Build a stable, human-readable base name for a study export, e.g.
 * `Winston_Kritzer_2026-01-12_CT`. Used as the archive name and the per-study
 * folder when several studies are exported together.
 */
export function studyBaseName(study) {
    const parts = [
        study.patient.name,
        studyDatePart(study.study.dateTime),
        study.study.modality,
    ].filter((p) => !!p && p.trim().length > 0);
    return sanitizeFilename(parts.join(' '), `study_${study.id.slice(0, 8)}`);
}
/** A number zero-padded to two digits for a file name (`3` → `03`); non-finite reads as `00`. */
function twoDigits(n) {
    return String(Number.isFinite(n) ? n : 0).padStart(2, '0');
}
/**
 * Folder (and file stem) for a series within a study archive:
 * `series_{number}_{description, else modality}`, e.g. `series_2_Thorax`.
 *
 * A series that is one split-out cine loop (see `seriesLoopInstanceNumber`)
 * also carries the loop's instance number, `series_1_US_clip_17`: ingest gives
 * every loop of an echo exam a series row of its own with the SAME number and
 * description, so without it every loop's files would land in one folder.
 */
export function seriesFolderName(series) {
    const label = series.seriesDescription || series.modality || 'series';
    const loop = seriesLoopInstanceNumber(series);
    const clip = loop == null ? '' : `_clip_${twoDigits(loop)}`;
    return sanitizeFilename(`series_${series.seriesNumber ?? 0}_${label}${clip}`, `series_${series.id.slice(0, 8)}`);
}
/**
 * File name for one exported cine clip.
 *
 * - A series that is itself one loop already names its clip in the folder
 *   name, so the file is just that: `series_1_US_clip_17.mp4`.
 * - A loop inside a series that holds several (a series filed before ingest
 *   split loops out) adds the loop's instance number: `series_1_US_clip_03.avi`.
 * - A clip that is a whole series (a single-frame stack) is named after the
 *   series alone: `series_4_CT.mp4`.
 */
export function clipFileName(clip, ext) {
    const series = seriesFolderName(clip.series);
    if (!clip.instance || seriesLoopInstanceNumber(clip.series) != null)
        return `${series}.${ext}`;
    return `${series}_clip_${twoDigits(clip.instance.instanceNumber)}.${ext}`;
}
/**
 * True when the study holds exactly one instance (one series, one image), whatever
 * its frame count. Counts through `seriesImageCount`: a summary-loaded study has
 * `images: []` (or only the pages loaded so far) and the real total in `imageCount`.
 */
export function studyIsOneInstance(study) {
    const [only, ...rest] = study.series;
    return only !== undefined && rest.length === 0 && seriesImageCount(only) === 1;
}
/**
 * The name ONE exported file is saved under when it is the whole download:
 * the study's base name, then the file's path as it would sit in the archive,
 * folders flattened — `Adair_Ollie_2026-04-16_US_series_1_US_clip_17.mp4`. Every
 * single-file save that goes through the browser's download manager uses this
 * (the download dialog and the viewer's Save clip), so the same file gets the
 * same name from both. A save into a file handle the picker already opened keeps
 * the name the picker was given.
 *
 * `onlyPossibleFile` keeps the commonest downloads short. Set it when the study
 * could only ever produce this one file in the chosen format (a lone radiograph
 * as JPEG, a study that is one loop as MP4, a study with one report as PDF):
 * nothing needs telling apart, and the file is just
 * `Winston_Kritzer_2026-01-12_DX.jpg`. The caller decides, since only it knows
 * the format and the report count.
 */
export function singleFileName(study, path, { onlyPossibleFile }) {
    const base = studyBaseName(study);
    const dot = path.lastIndexOf('.');
    const ext = dot > path.lastIndexOf('/') ? path.slice(dot + 1) : '';
    if (onlyPossibleFile && ext)
        return `${base}.${ext}`;
    return `${base}_${path.replace(/\//g, '_')}`;
}
